import React from 'react'
import PropTypes from 'prop-types'

import { connect } from 'react-redux'
import * as firebase from 'firebase'

import Dialog from 'material-ui/Dialog'

import LineItemsDetailsHeader from './LineItemsDetailsHeader'
import LineItemsDetailsContent from './LineItemsDetailsContent'
import JobModuleBase from '../JobModuleBase'

class LineItemsDetails extends JobModuleBase {

  static propTypes = {
    open: PropTypes.bool.isRequired,
    teamMembers: PropTypes.array.isRequired,
    items: PropTypes.shape().isRequired,
    userId: PropTypes.string.isRequired,
    onRequestClose: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      mode: 'day',
      dateStr: props.jobInfo.modules.date.startDate,
      lineItems: this.getActiveLineItems(props.items),
      workers: this.getJobWorkers(props.jobInfo, props.teamMembers),
      allocations: {},
      loading: true
    }
  }

  getDayAllocationDbPath = (dateStr) => {
    return `${this.getExtendedModulesDbPath()}/allocation/${dateStr}`
  }

  /**
   * Pointer to the allocation table
   */
  getTotalsAllocationDbPath = () => {
    return `/allocation/${this.props.jobInfo.id}`
  }

  getItemWorkerAllocationDbPath = (lineItemId, workerId) => {
    return `${this.getDayAllocationDbPath(this.state.dateStr)}/${lineItemId}/${workerId}`
  }

  componentWillMount() {
    this.addDayAllocationListener(this.state.dateStr)
  }

  componentWillUnmount() {
    this.removeDayAllocationListener(this.state.dateStr)
  }
  
  componentWillReceiveProps(nextProps) {
    // Handle props that change from the parent
    // component and set state appropriately.
    if (this.props.jobInfo !== nextProps.jobInfo
      || this.props.teamMembers !== nextProps.teamMembers
      || this.props.items !== nextProps.items) {
      this.setState({
        lineItems: this.getActiveLineItems(nextProps.items),
        workers: this.getJobWorkers(nextProps.jobInfo, nextProps.teamMembers)
      })
    }
  }

  addDayAllocationListener = (dateStr) => {
    firebase.database()
      .ref(this.getDayAllocationDbPath(dateStr))
      .on('value', this.onAllocationValue)
  }

  removeDayAllocationListener = (dateStr) => {
    firebase.database()
      .ref(this.getDayAllocationDbPath(dateStr))
      .off('value', this.onAllocationValue)
  }

  /**
   * Totals allocation and daily allocations share the same DB schema,
   * that is: 
   * 
   * lineItemId: {
   *   workerId: {
   *     timeInMinutes: number
   *   }
   * }
   * 
   * So we can share the same listener, infra to show daily breakdowns and totals.
   */
  addTotalsAllocationListener = () => {
    firebase.database()
      .ref(this.getTotalsAllocationDbPath())
      .on('value', this.onAllocationValue)
  }

  removeTotalsAllocationListener = () => {
    firebase.database()
      .ref(this.getTotalsAllocationDbPath())
      .off('value', this.onAllocationValue)
  }

  /**
   * Sorts workers in a line items allocation details putting
   * first the members and then the laborers.
   * 
   * Each groud is sorted by name.
   */
  workersComparer = (w1, w2) => {
    if (w1.type !== w2.type) {
      // types are 'member' and 'laborer'
      return w2.type.localeCompare(w1.type)
    }
    return w1.name.localeCompare(w2.name, 'co', { sensitivity: 'base' })
  }

  sortedUnion = (workersList, laborersList, teamMembers) => {
    const result = []
    for (const workerId in workersList) {
      const member = teamMembers.find(m => m.userId === workerId)
      if (member) {
        result.push({
          type: 'member',
          id: workerId,
          name: member.displayName
        })
      }
    }
    for (const laborerId in laborersList) {
      result.push({
        type: 'laborer',
        id: laborerId,
        name: laborerId
      })
    }
    result.sort(this.workersComparer)
    return result
  }

  getJobWorkers = (jobInfo, teamMembers) => {
    const workersModule = jobInfo.modules.workers
    const laborersModule = jobInfo.modules.laborers
    const workersList = workersModule && workersModule.list || {}
    const laborersList = laborersModule && laborersModule.list || {}
    return this.sortedUnion(workersList, laborersList, teamMembers)
  }

  getActiveLineItems = (items) => {
    const result = []
    for (const itemId in items) {
      const item = items[itemId]
      if (!item.deleted) {
        result.push(item)
      }
    }
    return result
  }

  onAllocationValue = (allocationSnapshot) => {
    const allocations = allocationSnapshot && allocationSnapshot.val() || {}
    this.setState({
      loading: false,
      allocations
    })
  }

  handleDetailsCloseRequested = () => {
    this.props.onRequestClose()
  }

  handleModeSelectorChanged = (mode) => {
    if (mode !== this.state.mode) {
      // Changed from Day to Totals
      if (this.state.mode === 'day') {
        this.removeDayAllocationListener(this.state.dateStr)
        this.addTotalsAllocationListener()
      }
      // Changed from Totals to Day
      else {
        this.removeTotalsAllocationListener()        
        this.addDayAllocationListener(this.state.dateStr)
      }
      this.setState({
        mode
      })
    }
  }

  handleSelectedDayChanged = (newDateStr) => {
    this.removeDayAllocationListener(this.state.dateStr)
    this.addDayAllocationListener(newDateStr)
    this.setState({
      dateStr: newDateStr
    })
  }

  handleAllocationTimeChanged = (lineItem, worker, timeInMinutes) => {
    const allocationRef = firebase.database()
      .ref(this.getItemWorkerAllocationDbPath(lineItem.id, worker.id))
    allocationRef.update({
      type: worker.type,
      timeInMinutes,
      timestamp: firebase.database.ServerValue.TIMESTAMP,
      editorId: this.props.userId
    })
  }

  render() {
    return (
      <Dialog
        open={this.props.open}
        modal={false}
        contentStyle={styles.container}
        bodyStyle={styles.body}
        onRequestClose={this.handleDetailsCloseRequested}>
        <LineItemsDetailsHeader
          onRequestClose={this.handleDetailsCloseRequested}/>
        <LineItemsDetailsContent
          loading={this.state.loading}
          mode={this.state.mode}
          dateStr={this.state.dateStr}
          jobInfo={this.props.jobInfo}
          lineItems={this.state.lineItems}
          workers={this.state.workers}
          allocations={this.state.allocations}
          onModeSelectorChanged={this.handleModeSelectorChanged}
          onSelectedDayChanged={this.handleSelectedDayChanged}
          onAllocationTimeChanged={this.handleAllocationTimeChanged} />
      </Dialog>
    )
  }

}

const styles = {

  body: {
    padding: 0
  },

  container: {
    borderRadius: 2,
    // This is a hack as there is a bug in Dialog's definition to properly
    // apply the border radius.
    overflow: 'auto'
  }

}

const mapStateToProps = (state) => ({
  teamMembers: state.firebase.team.members.value,
  userId: state.firebase.userAuth.id
})

export default connect(mapStateToProps)(LineItemsDetails)



// WEBPACK FOOTER //
// ./src/schedule/common/modules/lineItems/LineItemsDetails.js